import BalanceHistory from '../components/BalanceHistory'
import { loadBalanceSnapshots } from '../lib/loadBalanceSnapshots'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useAccountNicknames } from '../hooks/useAccountNicknames'
import { usePrivacy } from '../hooks/usePrivacy'
import AccountName from '../components/AccountName'
import PrivateValue from '../components/PrivateValue'
import PortfolioAllocation from '../components/PortfolioAllocation'
import {
  buildPortfolioAllocation,
  computePriorClosePct,
  getAccountCash,
  getAccountValue,
  summarizeDayChange,
} from '../lib/portfolioAnalytics'

function fmt(val) {
  if (val == null) return '—'
  const n = Number(val)
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`
  return `$${n.toFixed(2)}`
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="bg-canvas border border-border rounded-xl p-5">
      <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">{label}</div>
      <div className={`font-mono text-2xl font-semibold ${accent || 'text-ink'}`}>{value}</div>
      {sub && <div className="font-mono text-xs text-inkMuted mt-1">{sub}</div>}
    </div>
  )
}

export default function AccountsSummary() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { getNickname } = useAccountNicknames()
  const { privacyMode } = usePrivacy()
  const mask = (val) => privacyMode ? '•••••' : val
  const [accounts, setAccounts] = useState([])
  const [positionData, setPositionData] = useState([])
  const [positionCounts, setPositionCounts] = useState({})
  const [snapshots, setSnapshots] = useState([])
  const [snapshotError, setSnapshotError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [connected, setConnected] = useState(null)

  const loadData = useCallback(async () => {
    if (!user) return
    if (accounts.length === 0) setLoading(true)

    const [{ data: tokenRow }, { data: accts }, { data: pos }, { data: snaps, error: snapsError }] = await Promise.all([
      supabase
        .from('broker_tokens')
        .select('broker')
        .eq('user_id', user.id)
        .eq('broker', 'schwab')
        .maybeSingle(),
      supabase
        .from('schwab_accounts')
        .select('account_number, nick_name, account_type, equity')
        .eq('user_id', user.id)
        .order('account_number'),
      supabase
        .from('schwab_positions')
        .select('account_number, symbol, instrument_type, quantity, average_price, market_value, current_day_pnl, prev_close_price, raw, status, cost_basis_long, cost_basis_short, unrealized_pnl_long, unrealized_pnl_short, cost_basis_source, currency, contract_multiplier')
        .eq('user_id', user.id)
        .or('status.eq.open,status.is.null'),
      loadBalanceSnapshots(supabase, user.id),
    ])

    setConnected(!!tokenRow)
    setAccounts(accts || [])
    setPositionData(pos || [])
    setSnapshots(snaps || [])
    setSnapshotError(snapsError)

    const counts = {}
    for (const p of (pos || [])) {
      counts[p.account_number] = (counts[p.account_number] || 0) + 1
    }
    setPositionCounts(counts)

    setLoading(false)
  }, [user])

  useEffect(() => { loadData() }, [loadData])

  // ── Derived totals ────────────────────────────────────────────────────────
  const totalEquity = accounts.reduce((s, a) => s + getAccountValue(a), 0)
  const { byAccount: dayChangeByAcct, totalDayChange, totalDayPct: dayChangePct } = useMemo(
    () => summarizeDayChange(accounts, positionData),
    [accounts, positionData]
  )
  const allocation = useMemo(
    () => buildPortfolioAllocation(accounts, positionData, getNickname),
    [accounts, positionData, getNickname]
  )

  const dayColor = totalDayChange > 0 ? 'text-forest' : totalDayChange < 0 ? 'text-ember' : 'text-ink'

  return (
    <div className="space-y-6">
      {(loading || !connected || accounts.length === 0) && <div className="card text-sm text-inkMuted" role="status">
        {loading ? 'Loading accounts…' : !connected ? 'Broker disconnected. Saved balance history remains available.' : 'No account data synced yet.'}
        {!loading && <Link to="/integrations" className="underline ml-2">Manage connection</Link>}
      </div>}
      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label="Total Value"
          value={<PrivateValue>{accounts.length ? fmt(totalEquity) : '—'}</PrivateValue>}
          sub={`${accounts.length} account${accounts.length !== 1 ? 's' : ''}`}
        />
        <StatCard
          label="Day Change $"
          value={<PrivateValue>{accounts.length ? (totalDayChange >= 0 ? '+' : '') + fmt(totalDayChange) : '—'}</PrivateValue>}
          sub="vs. prior close"
          accent={dayColor}
        />
        <StatCard
          label="Day Change %"
          value={<PrivateValue>{accounts.length ? (dayChangePct >= 0 ? '+' : '') + dayChangePct.toFixed(2) + '%' : '—'}</PrivateValue>}
          sub="vs. prior close"
          accent={dayColor}
        />
      </div>

      <BalanceHistory snapshots={snapshots} error={snapshotError} loading={loading} />

      {/* Accounts table */}
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Breakdown</div>
          <h2 className="font-black text-base uppercase tracking-wide text-ink">Account Summary</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                {['Account', 'Type', 'Equity', 'Cash', 'Positions', 'Day Change $', 'Day Change %'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {accounts.map((acct, i) => {
                const equity = getAccountValue(acct)
                const cash = getAccountCash(acct)
                const dayChange = dayChangeByAcct[acct.account_number] ?? 0
                const dayPct = computePriorClosePct(dayChange, equity)
                const count = positionCounts[acct.account_number] ?? 0
                const dc = dayChange > 0 ? 'text-forest' : dayChange < 0 ? 'text-ember' : 'text-inkMuted'
                return (
                  <tr key={acct.account_number} onClick={() => navigate(`/accounts/${acct.account_number.slice(-4)}`)} className={`border-b border-border last:border-0 hover:bg-canvas2 transition-colors cursor-pointer ${i % 2 === 0 ? '' : 'bg-canvas2/20'}`}>
                    <td className="px-4 py-4">
                      <AccountName accountNumber={acct.account_number} className="font-mono text-sm font-semibold text-ink" />
                    </td>
                    <td className="px-4 py-4 font-mono text-xs text-inkMuted">{acct.account_type || '—'}</td>
                    <td className="px-4 py-4 font-mono text-sm font-semibold text-ink"><PrivateValue>{fmt(equity)}</PrivateValue></td>
                    <td className="px-4 py-4 font-mono text-sm text-inkMuted"><PrivateValue>{fmt(cash)}</PrivateValue></td>
                    <td className="px-4 py-4 font-mono text-sm text-inkMuted">{count}</td>
                    <td className={`px-4 py-4 font-mono text-sm font-semibold ${dc}`}>
                      <PrivateValue>{dayChange !== 0 ? (dayChange >= 0 ? '+' : '') + fmt(dayChange) : '—'}</PrivateValue>
                    </td>
                    <td className={`px-4 py-4 font-mono text-sm ${dc}`}>
                      <PrivateValue>{dayChange !== 0 ? (dayPct >= 0 ? '+' : '') + dayPct.toFixed(2) + '%' : '—'}</PrivateValue>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-border bg-canvas2">
                <td colSpan={2} className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-inkMuted">
                  {accounts.length} account{accounts.length !== 1 ? 's' : ''}
                </td>
                <td className="px-4 py-3 font-mono text-sm font-semibold text-ink"><PrivateValue>{fmt(totalEquity)}</PrivateValue></td>
                <td className="px-4 py-3" />
                <td className="px-4 py-3" />
                <td className={`px-4 py-3 font-mono text-sm font-semibold ${dayColor}`}>
                  <PrivateValue>{totalDayChange !== 0 ? (totalDayChange >= 0 ? '+' : '') + fmt(totalDayChange) : '—'}</PrivateValue>
                </td>
                <td className={`px-4 py-3 font-mono text-sm ${dayColor}`}>
                  <PrivateValue>{totalDayChange !== 0 ? (dayChangePct >= 0 ? '+' : '') + dayChangePct.toFixed(2) + '%' : '—'}</PrivateValue>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <PortfolioAllocation allocation={allocation} mask={mask} />
    </div>
  )
}
